import { HttpParams } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    OnDestroy,
    ViewEncapsulation,
    forwardRef,
    inject,
    input,
    viewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import EasyMDE from 'easymde';

import { BaseControlValueAccessor } from '~core/abstractions/base-control-value-accessor';
import { ErrorTooltipDirective } from '~core/directives/error-tooltip.directive';
import { SecurityStorage } from '~core/services/storages/security.storage';
import { Tools } from '~core/utils/tools/index';
import { environment } from '~environments/environment';

@Component({
    selector: 'arc-markdown-editor',
    templateUrl: './markdown-editor.component.html',
    styleUrls: ['../../assets/styles/easymde.min.css', '../../assets/styles/mde-styles.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MarkdownEditorComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [ErrorTooltipDirective]
})
export class MarkdownEditorComponent extends BaseControlValueAccessor<string> implements OnDestroy, AfterViewInit {
    readonly markdownEditor = viewChild.required('arcMarkdownEditor', { read: ElementRef });

    readonly styleClass = input<string>('mde-default');
    readonly allowImageUpload = input<boolean>(true);
    readonly isExpandable = input<boolean>(false);

    @HostBinding('class')
    get hostClasses(): string {
        return `${this.styleClass()} ${this.isExpandable() ? 'mde-resizable' : ''}`.trim();
    }

    easyMDE?: EasyMDE;

    private resizeObserver?: ResizeObserver;

    private securityStorage = inject(SecurityStorage);

    ngAfterViewInit(): void {
        const userInfo = this.securityStorage.getUserInfo();
        const imageEndpointParams = new HttpParams().set('token', userInfo?.token || '');
        this.easyMDE = new EasyMDE({
            element: this.markdownEditor().nativeElement,
            initialValue: this.value ?? '',
            uploadImage: this.allowImageUpload(),
            imagePathAbsolute: true,
            imageMaxSize: 1024 * 1024 * 10,
            spellChecker: false,
            nativeSpellcheck: false,
            status: [], // removes the status bar
            imageUploadEndpoint: `${Tools.Url.getUrl('blobs', environment.baseUrl)}?${imageEndpointParams.toString()}`,
            previewClass: ['arc-markdown', 'p-4', 'bg-white']
        });

        this.easyMDE.codemirror.on('change', () => {
            const raw = this.easyMDE?.value() ?? '';
            this.valueChanged(this.normalizeForStorage(raw));
        });

        if (this.isExpandable()) {
            // native resize handle doesn't trigger CodeMirror's own size recalculation
            this.resizeObserver = new ResizeObserver(() => this.easyMDE?.codemirror.refresh());
            this.resizeObserver.observe(this.easyMDE.codemirror.getWrapperElement());
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.resizeObserver?.disconnect();
        this.easyMDE?.cleanup();
    }

    override writeValue(value: string): void {
        super.writeValue(value);
        this.easyMDE?.value(value ?? '');
    }

    focus(): void {
        this.easyMDE?.codemirror.focus();
    }

    private normalizeForStorage(v: string): string {
        const lf = v.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        return lf.replace(/\n/g, '\r\n');
    }
}
